import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowUpRight,
  BadgeCheck,
  CalendarClock,
  IndianRupee,
  Mail,
  MapPin,
  Percent,
  Phone,
  TrendingUp,
  UserRound,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { KpiCard, KpiSkeleton } from "../../components/KpiCard";
import { PieCard } from "../../components/PieCard";
import { ClientBadge, LevelBadge, StatusBadge } from "../../components/Badges";
import { EmptyState } from "../../components/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { api, apiError, mediaUrl } from "../../lib/api";
import { formatDate, formatINR, formatPct, initials } from "../../lib/format";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get("/api/client/dashboard")
      .then(({ data: payload }) => {
        if (active) setData(payload);
      })
      .catch((error) => toast.error(apiError(error, "Could not load your dashboard")))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="page-loading-skeleton">
        <Skeleton className="h-28 w-full rounded-xl" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <KpiSkeleton count={5} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={Wallet}
        title="Dashboard unavailable"
        description="We could not load your portfolio right now. Please refresh the page."
        action={<Button onClick={() => window.location.reload()}>Refresh</Button>}
      />
    );
  }

  const { profile, manager, metrics, charts, recentInvestments, recentWithdrawals } = data;
  const photo = mediaUrl(profile.photo);
  const badges = profile.badges || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Portfolio overview"
        subtitle="Figures are maintained by your relationship manager and updated each trading day."
      />

      <Card className="border-border">
        <CardContent className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-lg font-semibold">
            {photo ? (
              <img src={photo} alt={profile.name} className="h-full w-full object-cover" />
            ) : (
              initials(profile.name)
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold" data-testid="profile-name">
                {profile.name}
              </h2>
              <LevelBadge level={profile.level} />
              {badges.map((b) => (
                <ClientBadge key={b} label={b} />
              ))}
            </div>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 break-words" data-testid="profile-email">
                  {profile.email}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{profile.phone || "Not provided"}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">{profile.address || "Not provided"}</span>
              </div>
              <div className="flex items-start gap-2">
                <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span data-testid="profile-manager">
                  {manager ? (
                    <>
                      <span className="text-muted-foreground">Manager: </span>
                      {manager.name}
                    </>
                  ) : (
                    "No manager assigned"
                  )}
                </span>
              </div>
            </dl>
          </div>
          <div className="shrink-0 rounded-lg border border-border surface-2 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Portfolio value</p>
            <p className="font-num mt-1 text-xl font-semibold" data-testid="portfolio-value">
              {formatINR(metrics.portfolioValue)}
            </p>
            <p className="mt-1 flex items-center gap-1 text-xs text-[hsl(var(--profit))]">
              <ArrowUpRight className="h-3 w-3" />
              {formatINR(metrics.overallProfitAmount)} total profit
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard
          label="Today's profit"
          value={formatINR(metrics.todayProfitAmount)}
          icon={TrendingUp}
          tone="profit"
          hint="Credited for the latest trading day"
          testId="kpi-today-profit"
        />
        <KpiCard
          label="Today's profit %"
          value={formatPct(metrics.todayProfitPercent)}
          icon={Percent}
          tone="profit"
          hint="Daily return on capital"
          testId="kpi-today-profit-percent"
        />
        <KpiCard
          label="Total invested"
          value={formatINR(metrics.totalInvested)}
          icon={IndianRupee}
          hint="Capital currently deployed"
          testId="kpi-total-invested"
        />
        <KpiCard
          label="Overall profit %"
          value={formatPct(metrics.overallProfitPercent)}
          icon={ArrowUpRight}
          tone="profit"
          hint={`${formatINR(metrics.overallProfitAmount)} since inception`}
          testId="kpi-overall-profit-percent"
        />
        <KpiCard
          label="Next withdrawal"
          value={formatDate(metrics.nextWithdrawalDate)}
          icon={CalendarClock}
          hint="Expected date, subject to approval"
          testId="kpi-next-withdrawal"
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PieCard
          title="Investment breakdown"
          subtitle="Capital grouped by entry type"
          data={charts.investmentBreakdown}
          testId="investment-breakdown-pie-chart"
          emptyTitle="No investment entries yet"
          emptyDescription="Your manager will add entries as capital is deployed."
        />
        <PieCard
          title="Returns overview"
          subtitle="Invested capital against accumulated profit"
          data={charts.returnsOverview}
          testId="returns-overview-pie-chart"
          emptyTitle="No returns to show yet"
          emptyDescription="Once capital is invested, returns will appear here."
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Recent investments</CardTitle>
            <Button asChild variant="ghost" size="sm" data-testid="dashboard-view-investments">
              <Link to="/investments">View all</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentInvestments?.length ? (
              <ul className="divide-y divide-border">
                {recentInvestments.map((row) => (
                  <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{row.type}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(row.date)}</p>
                    </div>
                    <p className="font-num text-sm">{formatINR(row.amount)}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={TrendingUp}
                title="No investments recorded"
                description="Entries added by your manager will appear here."
                className="border-0 bg-transparent py-6"
                testId="dashboard-investments-empty"
              />
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Recent withdrawals</CardTitle>
            <Button asChild variant="ghost" size="sm" data-testid="dashboard-view-withdrawals">
              <Link to="/withdrawals">View all</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentWithdrawals?.length ? (
              <ul className="divide-y divide-border">
                {recentWithdrawals.map((row) => (
                  <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{row.method}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(row.date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={row.status} />
                      <p className="font-num text-sm">{formatINR(row.amount)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={ArrowDownToLine}
                title="No withdrawals yet"
                description="Requests processed by your manager will be listed here."
                className="border-0 bg-transparent py-6"
                testId="dashboard-withdrawals-empty"
              />
            )}
          </CardContent>
        </Card>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-border surface-2 p-3 text-xs text-muted-foreground">
        <BadgeCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--profit))]" />
        All values are read-only and verified by Invextiing. If a figure looks incorrect, contact your
        relationship manager for review.
      </p>
    </div>
  );
}
